import { useState } from "react";
import { MiWorkCard } from "@miplatform/components";
import { ORDER_STATUS, ORDER_STATUS_COLORS_MAP } from "@miplatform/constants";
import { PopperPlacement } from "@miplatform/types";
import {
  mdiArrowULeftTop,
  mdiCalendarStartOutline,
  mdiCancel,
  mdiCheck,
  mdiFormatListBulleted,
  mdiHandBackRightOutline,
} from "@mdi/js";
import { useTheme } from "@mui/material";
import { CalendarSetsConfigDataItem } from "../../stores/calendarsStore/calendarsStore.types";
import { UndoApproveModal } from "../Orders/OrdersList/ActionMenu/UndoApproveModal";
import { UndoAssignmentModal } from "../Orders/OrdersList/ActionMenu/UndoAssignmentModal";
import { UndoConfirmationModal } from "../Orders/OrdersList/ActionMenu/UndoConfirmationModal";
import { useOrdersListActionMenuViewModel } from "../Orders/OrdersList/ActionMenu/useOrdersListActionMenuViewModel";
import { NhcCancelOrderModal } from "./NhcOrderCardModals/NhcCancelOrderModal";
import { NhcForceCompleteOrderModal } from "./NhcOrderCardModals/NhcForceCompleteOrderModal";
import { NhcHoldOrderModal } from "./NhcOrderCardModals/NhcHoldOrderModal";
import { NhcRescheduleOrderModal } from "./NhcOrderCardModals/NhcRescheduleOrderModal";

interface NhcOrderCardProps {
  data: any;
  headerColor?: string;
  menuViewConfig?: Record<string, boolean>;
  onRefresh: () => void;
  setSelectedOrder?: (order: any) => void;
  orderReasons: {
    holdReasons: { value: string; label: string }[];
    rescheduleReasons: { value: string; label: string }[];
  };
  popperPlacement?: PopperPlacement;
  popperComponent?: React.ReactNode;
  calendarSet: CalendarSetsConfigDataItem | null;
  isInBucket?: boolean;
  holdType?: string;
}

export const NhcOrderCard = ({
  data,
  headerColor,
  menuViewConfig = {
    Reschedule: true,
    Hold: true,
    Cancel: true,
    OrderActivityDetails: true,
    ForceComplete: true,
    UndoConfirmation: true,
    UndoApprove: true,
    UndoAssignment: true,
  },
  onRefresh,
  setSelectedOrder,
  isInBucket,
  orderReasons,
  popperPlacement,
  popperComponent,
  calendarSet,
  holdType,
}: NhcOrderCardProps) => {
  const orderId = data?.assignment?.order?.orderId;
  const orderStatus = data?.orderStatus;
  const modifiedOn = data?.assignment?.order?.modifiedOn;
  const theme = useTheme();

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isForceCompleteModalOpen, setIsForceCompleteModalOpen] =
    useState(false);
  const [isUndoConfirmationModalOpen, setIsUndoConfirmationModalOpen] =
    useState(false);
  const [isUndoApproveModalOpen, setIsUndoApproveModalOpen] = useState(false);
  const [isUndoAssignmentModalOpen, setIsUndoAssignmentModalOpen] =
    useState(false);

  const {
    isLoading,
    rescheduleOrder,
    holdOrder,
    cancelOrder,
    forceCompleteOrder,
    undoConfirmation,
    undoApprove,
    undoAssignment,
  } = useOrdersListActionMenuViewModel({ onRefresh });

  const handleReschedule = async (data: {
    contactName: string;
    rescheduleReasonId: string;
    rescheduleDate: string;
  }) => {
    const response = await rescheduleOrder(orderId, {
      ...data,
      modifiedOn,
    });
    setIsRescheduleModalOpen(false);
    if (response) {
      onRefresh();
    }
  };

  const handleHold = async (data: {
    contactName: string;
    reason: string;
    description: string;
    followUpDate: string;
    calendarId: string;
  }) => {
    const response = await holdOrder(orderId, {
      ...data,
      modifiedOn,
    });
    setIsHoldModalOpen(false);
    if (response) {
      onRefresh();
    }
  };

  const handleCancel = async (data: { reason: string }) => {
    const response = await cancelOrder(orderId, { ...data, modifiedOn });
    setIsCancelModalOpen(false);
    if (response) {
      onRefresh();
    }
  };

  const handleForceComplete = async () => {
    await forceCompleteOrder({ orderId, modifiedOn });
    setIsForceCompleteModalOpen(false);
  };

  const handleUndoConfirmation = async (data: { reason: string }) => {
    const response = await undoConfirmation(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoConfirmationModalOpen(false);
    if (response) {
      onRefresh();
    }
  };

  const handleUndoAssignment = async (data: { reason: string }) => {
    const response = await undoAssignment(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoAssignmentModalOpen(false);
    if (response) {
      onRefresh();
    }
  };

  const handleUndoApprove = async (data: { reason: string }) => {
    await undoApprove(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoApproveModalOpen(false);
  };

  const isOrderCompleted = orderStatus === ORDER_STATUS.COMPLETE;
  const isOrderCancelled = orderStatus === ORDER_STATUS.CANCELLED;
  const isOrderInFinalState = isOrderCompleted || isOrderCancelled;

  const actionMenuItems = [
    {
      label: "Change Requested Date",
      icon: mdiCalendarStartOutline,
      color: theme.palette.blue.dark,
      action: () => setIsRescheduleModalOpen(true),
      hide: !menuViewConfig.Reschedule || isOrderInFinalState,
    },
    {
      label: "Hold",
      icon: mdiHandBackRightOutline,
      color: theme.palette.purple.dark,
      action: () => setIsHoldModalOpen(true),
      hide: !menuViewConfig.Hold || isOrderInFinalState,
    },
    {
      label: "Force Closeout",
      icon: mdiCheck,
      color: theme.palette.success.main,
      action: () => setIsForceCompleteModalOpen(true),
      hide: !menuViewConfig.ForceComplete || isOrderInFinalState,
    },
    {
      label: "Details",
      icon: mdiFormatListBulleted,
      color: theme.palette.grey[500],
      action: () => {
        if (setSelectedOrder) {
          setSelectedOrder(data);
        }
      },
      hide: !menuViewConfig.OrderActivityDetails,
    },
    {
      label: "Cancel",
      icon: mdiCancel,
      color: theme.palette.red.dark,
      action: () => setIsCancelModalOpen(true),
      hide: !menuViewConfig.Cancel || isOrderInFinalState,
    },
    {
      label: "Undo Confirmation",
      icon: mdiArrowULeftTop,
      color: theme.palette.orange.main,
      action: () => setIsUndoConfirmationModalOpen(true),
      hide:
        !menuViewConfig.UndoConfirmation ||
        orderStatus !== ORDER_STATUS.CONFIRMED,
    },
    {
      label: "Undo Approve",
      icon: mdiArrowULeftTop,
      color: theme.palette.orange.main,
      action: () => setIsUndoApproveModalOpen(true),
      hide:
        !menuViewConfig.UndoApprove || orderStatus !== ORDER_STATUS.APPROVED,
    },
    {
      label: "Undo Assignment",
      icon: mdiArrowULeftTop,
      color: theme.palette.orange.main,
      action: () => setIsUndoAssignmentModalOpen(true),
      hide:
        !menuViewConfig.UndoAssignment || orderStatus !== ORDER_STATUS.ASSIGNED,
    },
  ];

  return (
    <>
      <UndoConfirmationModal
        open={isUndoConfirmationModalOpen}
        onClose={() => setIsUndoConfirmationModalOpen(false)}
        onUndo={handleUndoConfirmation}
        isLoading={isLoading}
      />
      <UndoApproveModal
        open={isUndoApproveModalOpen}
        onClose={() => setIsUndoApproveModalOpen(false)}
        onUndo={handleUndoApprove}
        isLoading={isLoading}
      />
      <UndoAssignmentModal
        open={isUndoAssignmentModalOpen}
        onClose={() => setIsUndoAssignmentModalOpen(false)}
        onUndo={handleUndoAssignment}
        isLoading={isLoading}
      />
      <NhcRescheduleOrderModal
        open={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onReschedule={handleReschedule}
        orderReasons={orderReasons}
        isLoading={isLoading}
        currentScheduledDate={data?.scheduledStartDate}
      />

      <NhcHoldOrderModal
        open={isHoldModalOpen}
        onClose={() => setIsHoldModalOpen(false)}
        onHold={handleHold}
        isLoading={isLoading}
        calendarSet={calendarSet?.config ?? null}
      />

      <NhcCancelOrderModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onCancel={handleCancel}
        isLoading={isLoading}
      />

      <NhcForceCompleteOrderModal
        open={isForceCompleteModalOpen}
        onClose={() => setIsForceCompleteModalOpen(false)}
        onForceComplete={handleForceComplete}
        isLoading={isLoading}
      />

      <MiWorkCard
        title={data?.assignment?.order?.orderTemplateName || ""}
        status={data?.orderStatus}
        home={data?.home}
        statusConfig={ORDER_STATUS_COLORS_MAP}
        statusBottomText={holdType}
        headerColor={headerColor ?? theme.palette.grey.primary}
        menuItems={actionMenuItems}
        isRequestedInDateRange={data?.isRequestedInDateRange}
        isInBucket={isInBucket}
        popperPlacement={popperPlacement}
        popperComponent={popperComponent}
      />
    </>
  );
};
